import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { slugify } from "../../lib/api";

const inputCls =
  "w-full px-3.5 py-2.5 rounded-lg bg-[#0B0F19] border border-[#1F2937] text-sm text-[#F9FAFB] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#10B981]/40 focus:border-[#10B981] transition";

const CmsPage = ({ title, singular, endpoint, fields, columns, testid, defaultValues = {} }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch(() => toast.error(`Could not load ${title.toLowerCase()}`));
  useEffect(() => {
    load();
  }, [endpoint]);

  const openNew = () => {
    setEditing(null);
    const init = { ...defaultValues };
    fields.forEach((f) => {
      if (f.type === "checkbox") init[f.name] = init[f.name] ?? false;
      else if (f.type === "list") init[f.name] = "";
      else init[f.name] = init[f.name] ?? "";
    });
    setForm(init);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const f = { ...item };
    fields.forEach((field) => {
      if (field.type === "list") f[field.name] = Array.isArray(item[field.name]) ? item[field.name].join("\n") : "";
      if (field.type === "checkbox") f[field.name] = !!item[field.name];
    });
    setForm(f);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (f.type === "list") v = (v || "").split("\n").map((s) => s.trim()).filter(Boolean);
      if (f.type === "checkbox") v = !!v;
      payload[f.name] = v;
    });
    if (payload.title !== undefined && !payload.slug) payload.slug = slugify(payload.title);
    if (payload.name !== undefined && !payload.slug) payload.slug = slugify(payload.name);
    if (payload.question !== undefined && payload.slug !== undefined && !payload.slug) payload.slug = slugify(payload.question);
    try {
      if (editing) {
        await api.put(`/admin/${endpoint}/${editing.id}`, payload);
        toast.success(`${singular} updated`);
      } else {
        await api.post(`/admin/${endpoint}`, payload);
        toast.success(`${singular} created`);
      }
      setOpen(false);
      load();
    } catch {
      toast.error(`Could not save ${singular.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${singular.toLowerCase()} permanently?`)) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      toast.success(`${singular} deleted`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.put(`/admin/${endpoint}/${item.id}`, { published: !item.published });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, published: !it.published } : it)));
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div data-testid={`${testid}-page`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-[#F9FAFB]">{title}</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{items.length} items</p>
        </div>
        <button
          onClick={openNew}
          data-testid={`${testid}-add-btn`}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#10B981] text-[#0B0F19] text-sm font-bold hover:bg-[#0ea371] transition-colors"
        >
          <Plus size={16} /> Add {singular}
        </button>
      </div>

      <div className="mt-6 bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <p className="px-5 py-10 text-sm text-[#6B7280]">No items yet. Add your first {singular.toLowerCase()}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#1F2937]">
                  {columns.map((c) => <th key={c.key} className="px-5 py-3">{c.label}</th>)}
                  <th className="px-5 py-3">Published</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-[#1F2937]/50 last:border-0 hover:bg-[#0B0F19]/40" data-testid={`${testid}-row-${item.id.slice(0, 8)}`}>
                    {columns.map((c) => (
                      <td key={c.key} className="px-5 py-3.5 text-[#9CA3AF] max-w-[260px] truncate">
                        {c.render ? c.render(item) : <span className={c.primary ? "font-semibold text-[#F9FAFB]" : ""}>{item[c.key] || "—"}</span>}
                      </td>
                    ))}
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => togglePublish(item)}
                        data-testid={`${testid}-publish-${item.id.slice(0, 8)}`}
                        className={`relative w-10 h-5.5 h-6 rounded-full transition-colors ${item.published ? "bg-[#10B981]" : "bg-[#1F2937]"}`}
                        title={item.published ? "Published" : "Unpublished"}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${item.published ? "left-[18px]" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(item)} data-testid={`${testid}-edit-${item.id.slice(0, 8)}`} className="p-2 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => remove(item)} data-testid={`${testid}-delete-${item.id.slice(0, 8)}`} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={`${testid}-modal`}>
          <form onSubmit={save} className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-[#080C14] border border-[#1F2937] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-extrabold text-lg text-[#F9FAFB]">
                {editing ? `Edit ${singular}` : `Add ${singular}`}
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 text-[#9CA3AF] hover:text-white" data-testid={`${testid}-modal-close`}>
                <X size={18} />
              </button>
            </div>
            {fields.map((f) => (
              <div key={f.name}>
                {f.type === "checkbox" ? (
                  <label className="flex items-center gap-2.5 text-sm font-semibold text-[#9CA3AF] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                      data-testid={`${testid}-field-${f.name}`}
                      className="w-4 h-4 accent-[#10B981]"
                    />
                    {f.label}
                  </label>
                ) : (
                  <>
                    <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">{f.label}</label>
                    {f.type === "textarea" || f.type === "list" ? (
                      <textarea
                        rows={f.type === "list" ? 5 : 4}
                        className={inputCls}
                        placeholder={f.placeholder || (f.type === "list" ? "One item per line" : "")}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        data-testid={`${testid}-field-${f.name}`}
                      />
                    ) : (
                      <input
                        className={inputCls}
                        placeholder={f.placeholder || ""}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        data-testid={`${testid}-field-${f.name}`}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
            <button
              type="submit"
              disabled={saving}
              data-testid={`${testid}-save-btn`}
              className="w-full py-3 rounded-lg bg-[#10B981] text-[#0B0F19] font-display font-bold text-sm hover:bg-[#0ea371] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving..." : editing ? `Update ${singular}` : `Create ${singular}`}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export const AdminCourses = () => (
  <CmsPage
    title="Courses"
    singular="Course"
    endpoint="courses"
    testid="admin-courses"
    defaultValues={{ published: true, featured: false }}
    columns={[
      { key: "title", label: "Title", primary: true },
      { key: "duration", label: "Duration" },
      { key: "level", label: "Level" },
      { key: "featured", label: "Featured", render: (c) => (c.featured ? "★ Featured" : "—") },
    ]}
    fields={[
      { name: "title", label: "Course Title *" },
      { name: "slug", label: "URL Slug (auto if blank)", placeholder: "e.g. tally-prime" },
      { name: "short_desc", label: "Short Description", type: "textarea" },
      { name: "description", label: "Full Description", type: "textarea" },
      { name: "duration", label: "Duration", placeholder: "e.g. 8–12 Weeks" },
      { name: "level", label: "Level", placeholder: "e.g. Beginner to Job-Ready" },
      { name: "badge", label: "Badge", placeholder: "e.g. Flagship Program" },
      { name: "category", label: "Category", placeholder: "e.g. Tally / GST / TDS" },
      { name: "image_url", label: "Image URL" },
      { name: "modules", label: "Modules / Curriculum", type: "list" },
      { name: "featured", label: "Mark as Featured", type: "checkbox" },
      { name: "published", label: "Published", type: "checkbox" },
    ]}
  />
);

export const AdminServices = () => (
  <CmsPage
    title="Services"
    singular="Service"
    endpoint="services"
    testid="admin-services"
    defaultValues={{ published: true }}
    columns={[
      { key: "name", label: "Name", primary: true },
      { key: "slug", label: "Slug" },
    ]}
    fields={[
      { name: "name", label: "Service Name *" },
      { name: "slug", label: "URL Slug (auto if blank)", placeholder: "e.g. gst-taxation" },
      { name: "short_desc", label: "Short Description", type: "textarea" },
      { name: "description", label: "Full Description", type: "textarea" },
      { name: "deliverables", label: "Deliverables", type: "list" },
      { name: "published", label: "Published", type: "checkbox" },
    ]}
  />
);

export const AdminBlog = () => (
  <CmsPage
    title="Blog / Knowledge Centre"
    singular="Post"
    endpoint="blog"
    testid="admin-blog"
    defaultValues={{ published: true, author: "SterlingNest Faculty" }}
    columns={[
      { key: "title", label: "Title", primary: true },
      { key: "category", label: "Category" },
      { key: "author", label: "Author" },
    ]}
    fields={[
      { name: "title", label: "Post Title *" },
      { name: "slug", label: "URL Slug (auto if blank)" },
      { name: "category", label: "Category", placeholder: "Tally / GST / TDS / Career / Finance" },
      { name: "author", label: "Author" },
      { name: "excerpt", label: "Excerpt", type: "textarea" },
      { name: "content", label: "Content (blank line between paragraphs)", type: "textarea" },
      { name: "published", label: "Published", type: "checkbox" },
    ]}
  />
);

export const AdminTestimonials = () => (
  <CmsPage
    title="Testimonials"
    singular="Testimonial"
    endpoint="testimonials"
    testid="admin-testimonials"
    defaultValues={{ published: true }}
    columns={[
      { key: "name", label: "Name", primary: true },
      { key: "role", label: "Role / Course" },
    ]}
    fields={[
      { name: "name", label: "Student / Client Name *" },
      { name: "role", label: "Designation / Role", placeholder: "e.g. Junior Accountant" },
      { name: "course", label: "Course Completed / Service" },
      { name: "photo_url", label: "Photo URL (optional)" },
      { name: "quote", label: "Testimonial *", type: "textarea" },
      { name: "published", label: "Published", type: "checkbox" },
    ]}
  />
);

export const AdminFaqs = () => (
  <CmsPage
    title="FAQs"
    singular="FAQ"
    endpoint="faqs"
    testid="admin-faqs"
    defaultValues={{ published: true }}
    columns={[
      { key: "question", label: "Question", primary: true },
      { key: "category", label: "Category" },
    ]}
    fields={[
      { name: "question", label: "Question *" },
      { name: "answer", label: "Answer *", type: "textarea" },
      { name: "category", label: "Category", placeholder: "Courses / Admissions / Careers" },
      { name: "published", label: "Published", type: "checkbox" },
    ]}
  />
);
